import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { NovaVenda } from './NovaVenda'
import { TxnModal } from './TxnModal'
import { ConciliacaoOfx } from './ConciliacaoOfx'
import { ReconciliationRules } from './ReconciliationRules'
import { type Kind, type Txn, brl, ptDate, LABELS, inputCls } from './finance-shared'
import { BankLogo } from './BankLogo'
import { PanelShell, PlnBarra, PlnBloco, stickyTh } from './PanelShell'
import { useApontamentos, ApontamentosColuna, FaixaConciliacao } from './Apontamentos'
import {
  FiPlus, FiSearch, FiArrowUpCircle, FiArrowDownCircle, FiEdit2, FiTrash2, FiLoader, FiCheck, FiShoppingCart,
  FiChevronLeft, FiChevronRight, FiCheckCircle, FiSlash, FiRotateCcw,
  FiList, FiRepeat, FiSliders, FiUploadCloud, FiAlertTriangle,
  FiChevronUp, FiChevronDown, FiCopy, FiX,
} from 'react-icons/fi'

// Contas a pagar (§5.4) e a receber (§5.5): a MESMA tela, discriminada por `kind`.
// O backend (`/api/transactions`) é idêntico; aqui só mudam os rótulos. Tipos e o
// modal de lançamento vivem em finance-shared/TxnModal (reusados na Competência).

type Bucket = '' | 'vencido' | 'vence_hoje' | 'a_vencer' | 'pago' | 'perda'
type EffStatus = 'vencido' | 'vence_hoje' | 'em_aberto' | 'agendado' | 'pago' | 'perda'

interface CardVal { count: number; amount: number }
interface Cards { vencido: CardVal; vence_hoje: CardVal; a_vencer: CardVal; pago: CardVal; perda: CardVal; total: CardVal }

const PAGE_SIZE = 20
const thisMonth = () => new Date().toISOString().slice(0, 7)
const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number)
  const s = new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  return s.charAt(0).toUpperCase() + s.slice(1) // "Maio de 2026" (não capitalizar o "de")
}
const addMonth = (m: string, n: number) => {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(Date.UTC(y, mo - 1 + n, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const STATUS_STYLE: Record<EffStatus, { label: string; cls: string }> = {
  vencido:    { label: 'Vencido',   cls: 'neu-tag bg-red-500/10 text-red-600 dark:text-red-400' },
  vence_hoje: { label: 'Vence hoje', cls: 'neu-tag bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  em_aberto:  { label: 'Em aberto', cls: 'neu-tag bg-secondary text-muted-foreground' },
  agendado:   { label: 'Agendado',  cls: 'neu-tag bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  pago:       { label: 'Pago',      cls: 'neu-tag bg-primary/10 text-primary' },
  perda:      { label: 'Perda',     cls: 'neu-tag bg-red-500/15 text-red-700 dark:text-red-300 line-through' },
}

type Tab = 'lancamentos' | 'conciliacao' | 'regras'

export function Contas({ kind }: { kind: Kind }) {
  const { authenticatedFetch } = useAuth()
  const L = LABELS[kind]
  // Conciliação bancária e regras só existem no lado de despesa (Contas a Pagar) —
  // é onde o extrato do banco entra hoje. Receita segue só com a tabela de lançamentos.
  const [tab, setTab] = useState<Tab>('lancamentos')
  const [items, setItems] = useState<Txn[]>([])
  const [cards, setCards] = useState<Cards | null>(null)
  // Quanto do resultado da busca veio de dentro de um rateio. Chave irmã de
  // `cards` na resposta, nunca somada nela — os seis baldes têm que continuar
  // fechando com o total.
  const [rateioBusca, setRateioBusca] = useState<{ lancamentos: number; partes: number; amount: number } | null>(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [month, setMonth] = useState(thisMonth())
  // Período personalizado DE/ATÉ (por vencimento) — alternativa ao mês
  const [periodMode, setPeriodMode] = useState<'mes' | 'custom'>('mes')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [search, setSearch] = useState('')
  const [bucket, setBucket] = useState<Bucket>('')
  const [page, setPage] = useState(1)
  // Filtro por conta bancária (pedido do diretor 2026-07-22, mesmo da
  // Conciliação). Server-side: o /api/transactions já aceita `account`, então
  // não precisa carregar tudo pra filtrar no cliente.
  const [accountFilter, setAccountFilter] = useState<number | ''>('')
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const [accounts, setAccounts] = useState<Array<{ id: number; name: string; bank_code: string | null; type?: string | null }>>([])
  useEffect(() => {
    authenticatedFetch('/api/bank-accounts').then(r => r.json()).then(d => {
      if (d.success) setAccounts((d.items || []).map((a: any) => ({ id: a.id, name: a.name, bank_code: a.bank_code || null, type: a.type || null })))
    }).catch(() => {})
  }, [authenticatedFetch])
  // Ordenação clicável (pedido do diretor 2026-07-10): vencimento ou situação
  const [sort, setSort] = useState<'due_date' | 'situacao'>('due_date')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')
  const toggleSort = (field: 'due_date' | 'situacao') => {
    if (sort === field) setDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSort(field); setDir('asc') }
  }
  const [modal, setModal] = useState<{ txn?: Txn } | null>(null)
  const [payTarget, setPayTarget] = useState<Txn | null>(null)
  const [showVenda, setShowVenda] = useState(false)
  const [importingErp, setImportingErp] = useState(false)
  const [erpMsg, setErpMsg] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null)
  const erpFileRef = useRef<HTMLInputElement>(null)

  // Duplicados (2026-07-22): o histórico do ERP e os títulos que a CONCILIAÇÃO
  // cria sozinha descrevem o mesmo pagamento, e nada impedia os dois de
  // coexistirem — foi assim que o saldo inflou R$ 3,19 mi e depois R$ 197 mil.
  // A varredura roda sozinha depois de todo import e avisa em modal.
  type DupRow = { id: number; description: string | null; reference_code: string | null; created_at: string; conciliada: number }
  type DupGroup = { payment_date: string; amount: number; kind: string; copias: number; lotes: number; bank_account_name: string | null; rows: DupRow[] }
  const [dupGroups, setDupGroups] = useState<DupGroup[] | null>(null)
  const [dupOpen, setDupOpen] = useState(false)
  const [dupChecking, setDupChecking] = useState(false)
  const [dupSel, setDupSel] = useState<Set<number>>(new Set())
  const [dupBusy, setDupBusy] = useState(false)
  const [dupMsg, setDupMsg] = useState<string | null>(null)

  // Apontamentos do setor (2026-07-27): as verificações do pente-fino das 06:00
  // ao lado dos cards, com ação e reconferência na hora. Só em Contas a Pagar —
  // é onde entra o extrato do banco e onde a duplicata nasce.
  // A faixa mensal segue o ANO em foco, não o mês: quem está em julho quer ver
  // que fevereiro ficou sem extrato.
  const anoFoco = Number((periodMode === 'custom' && fromDate ? fromDate : month).slice(0, 4)) || new Date().getFullYear()
  const apont = useApontamentos(kind, anoFoco, kind === 'despesa')
  // Movimento a focar quando o usuário vem de um apontamento para a aba de
  // conciliação. `token` faz o mesmo movimento poder ser refocado. `status`
  // diz em qual aba ele vive — um conciliado-sem-vínculo é 'conciliado' e
  // focá-lo na aba de pendentes mostraria uma lista onde ele não está.
  const [focusMov, setFocusMov] = useState<{ id: number; token: number; status?: 'pendente' | 'conciliado' } | null>(null)

  // Lançamentos com o rateio aberto. As partes já vêm na resposta da lista, então
  // abrir não custa requisição — é só mostrar.
  const [openSplits, setOpenSplits] = useState<Set<number>>(new Set())
  const toggleSplit = (id: number) => setOpenSplits(p => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n
  })

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const qs = new URLSearchParams({ kind, search, bucket, page: String(page), pageSize: String(PAGE_SIZE), sort, dir })
      if (accountFilter !== '') qs.set('account', String(accountFilter))
      if (periodMode === 'custom' && fromDate && toDate) { qs.set('from', fromDate); qs.set('to', toDate) }
      else qs.set('month', month)
      const r = await authenticatedFetch(`/api/transactions?${qs}`)
      const d = await r.json()
      // page 1 substitui (troca de filtro/mês); as seguintes ACUMULAM — a lista
      // é rolada, não paginada.
      if (d.success) {
        setItems(prev => (page === 1 ? d.items : [...prev, ...d.items])); setTotal(d.total); setCards(d.cards)
        setRateioBusca(d.rateio ?? null)
        // Abre sozinho o rateio de quem casou com a busca. Sem isto, procurar
        // "Combustíveis" traz a fatura do cartão — cuja descrição e categoria
        // não contêm "Combustíveis" em lugar nenhum visível — e a linha parece
        // um resultado errado. Página 1 zera; as seguintes só acrescentam, senão
        // rolar a lista fecharia o que a pessoa abriu na mão.
        setOpenSplits(prev => {
          const n = page === 1 ? new Set<number>() : new Set(prev)
          for (const it of d.items as Txn[]) if ((it.split_match_count || 0) > 0) n.add(it.id)
          return n
        })
      }
      else setError(d.error || 'Falha ao carregar')
    } catch { setError('Erro de conexão') }
    finally { setLoading(false) }
  }, [authenticatedFetch, kind, month, periodMode, fromDate, toDate, search, bucket, page, sort, dir, accountFilter])

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t) }, [load])

  // Recarrega SEMPRE da primeira leva. load() sozinho acumula quando page>1 —
  // com a lista rolada, excluir/pagar/editar reanexava as mesmas linhas (key
  // duplicada) e inflava items.length, o que fazia o scroll infinito parar
  // achando que tinha chegado ao fim e ESCONDER registros reais.
  const reload = useCallback(() => { setItems([]); setPage(p => (p === 1 ? p : 1)); if (page === 1) load() }, [page, load])
  useEffect(() => { setPage(1) }, [month, periodMode, fromDate, toDate, search, bucket, kind, accountFilter, sort, dir])

  // Scroll infinito (pedido do diretor 2026-07-22): rolar a lista puxa a página
  // seguinte em vez de trocar de página no rodapé. Dispara 200px antes do fim
  // pra próxima leva chegar antes de o diretor bater no fundo.
  // Trava própria: `loading` só fica true DEPOIS do debounce de 250ms, e nessa
  // janela cada evento de scroll incrementava a página — a rolagem rápida pulava
  // de 1 pra 5 e as páginas do meio nunca eram buscadas (buraco invisível na
  // lista). O ref fecha na hora e reabre quando a leva chega.
  const pagingRef = useRef(false)
  useEffect(() => { if (!loading) pagingRef.current = false }, [loading, items])
  const onListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (loading || pagingRef.current || items.length >= total) return
    const el = e.currentTarget
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) { pagingRef.current = true; setPage(p => p + 1) }
  }

  // Em tela alta, 20 linhas podem caber sem sobra — aí não há o que rolar e a
  // lista ficaria presa na primeira leva, parecendo que só existem 20. Enquanto
  // não houver scroll e houver mais registros, puxa a próxima.
  const listRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (loading || pagingRef.current || items.length === 0 || items.length >= total) return
    // Sem esta trava, um `total` desatualizado deixava a condição aberta pra
    // sempre e o efeito repetia a busca a cada resposta — loop de requisições.
    if (items.length < page * PAGE_SIZE) return
    const el = listRef.current
    if (el && el.scrollHeight <= el.clientHeight) { pagingRef.current = true; setPage(p => p + 1) }
  }, [items, loading, total, page])

  const remove = async (t: Txn) => {
    const series = t.recurrence_group_id && t.installment_total > 1
    const scope = series && confirm(`Excluir a SÉRIE inteira (${t.installment_total} lançamentos)?\n\nOK = série · Cancelar = só este`) ? 'series' : 'one'
    if (!confirm(scope === 'series' ? 'Confirma excluir a série?' : 'Confirma excluir este lançamento?')) return
    const r = await authenticatedFetch(`/api/transactions/${t.id}?scope=${scope}`, { method: 'DELETE' })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Falha'); return }
    reload()
  }

  const writeOff = async (t: Txn) => {
    const isPerda = t.effective_status === 'perda'
    if (!confirm(isPerda ? 'Reverter a baixa por perda (volta a em aberto)?' : `Baixar "${t.description}" como PERDA? Não será ${kind === 'receita' ? 'recebida' : 'paga'}.`)) return
    const r = await authenticatedFetch(`/api/transactions/${t.id}/write-off`, { method: 'POST' })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Falha'); return }
    reload()
  }

  // Histórico do ERP ("RELATÓRIO DE MOVIMENTO BANCÁRIO") — lançamentos já pagos,
  // despesas E receitas conforme a linha. Idempotente no backend (reference_code
  // ERPMOV): reenviar o mesmo arquivo substitui, não duplica.
  const onImportErp = async (file: File) => {
    setImportingErp(true); setErpMsg(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await authenticatedFetch('/api/import/erp-mov', { method: 'POST', body: fd })
      const d = await r.json()
      if (!d.success) { setErpMsg({ kind: 'err', text: d.error || 'Falha na importação' }); return }
      const avisos: string[] = []
      if (d.skippedTransfer) avisos.push(`${d.skippedTransfer} transferência(s) entre contas ignorada(s)`)
      if (d.semConta) avisos.push(`${d.semConta} linha(s) sem conta cadastrada (${d.contasNaoEncontradas.join(', ')})`)
      setErpMsg({
        kind: d.semConta ? 'warn' : 'ok',
        text: `${d.inserted} lançamento(s) importado(s)${d.removidosAnteriores ? ` (substituindo ${d.removidosAnteriores} anterior(es))` : ''}.${avisos.length ? ' ' + avisos.join('; ') + '.' : ''}`,
      })
      reload()
      checkDuplicates(true) // avisa se este import repetiu algo que já existia
    } catch { setErpMsg({ kind: 'err', text: 'Erro ao enviar o arquivo' }) }
    finally { setImportingErp(false); if (erpFileRef.current) erpFileRef.current.value = '' }
  }

  // Varre o banco atrás de repetições. `auto` = disparado por um import: só
  // incomoda com modal se houver grupo vindo de LOTES diferentes, que é o
  // padrão de import repetido. Repetição dentro do mesmo lote costuma ser
  // pagamento legítimo de mesmo valor no mesmo dia (comum aqui) e não merece
  // interromper ninguém.
  const checkDuplicates = async (auto = false) => {
    setDupChecking(true); setDupMsg(null)
    try {
      const r = await authenticatedFetch('/api/import/duplicates')
      const d = await r.json()
      if (!d.success) { if (!auto) { setDupMsg(d.error || 'Falha ao verificar'); setDupGroups([]); setDupOpen(true) } return }
      const groups: DupGroup[] = d.groups || []
      setDupGroups(groups)
      const suspeitos = groups.filter(g => g.lotes > 1)
      if (!auto || suspeitos.length > 0) {
        // Já vem marcado o que é seguro remover: as cópias mais NOVAS de cada
        // grupo cross-lote, preservando a primeira e nunca uma conciliada.
        const pre = new Set<number>()
        for (const g of suspeitos) {
          g.rows.filter(x => !x.conciliada).slice(1).forEach(x => pre.add(x.id))
        }
        setDupSel(pre)
        setDupOpen(true)
      }
    } catch { if (!auto) { setDupMsg('Erro ao verificar duplicados'); setDupGroups([]); setDupOpen(true) } }
    finally { setDupChecking(false) }
  }

  const removeDuplicates = async () => {
    if (!dupSel.size) return
    if (!confirm(`Remover ${dupSel.size} lançamento(s) duplicado(s)?\n\nLinhas conciliadas são preservadas, e ao menos uma cópia de cada pagamento permanece.`)) return
    setDupBusy(true)
    try {
      const r = await authenticatedFetch('/api/import/duplicates/remove', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...dupSel] }),
      })
      const d = await r.json()
      if (!d.success) { setDupMsg(d.error || 'Falha ao remover'); return }
      const resumo = `${d.removed} removido(s)${d.bloqueadasConciliadas ? ` · ${d.bloqueadasConciliadas} preservado(s) por estarem conciliados` : ''}.`
      setDupSel(new Set())
      await checkDuplicates(false)
      setDupMsg(resumo) // depois da revarredura: checkDuplicates limpa a mensagem
      reload()
      apont.recarregar() // o apontamento tem que sumir junto com a causa
    } catch { setDupMsg('Erro ao remover duplicados') }
    finally { setDupBusy(false) }
  }

  // Cada apontamento aponta pro lugar onde ele se resolve — é o que separa um
  // aviso de uma tarefa. Duplicata reusa o modal que já existe (com as guardas
  // de remoção); conciliação é a aba ao lado.
  const handleApontamento = (acao: string, payload?: { movimentoId?: number; status?: 'pendente' | 'conciliado' }) => {
    if (acao === 'duplicatas') checkDuplicates(false)
    else if (acao === 'conciliacao') {
      setTab('conciliacao')
      // Com movimento: a aba abre focada nele. Sem: abre a fila inteira, que é
      // o que o botão do cartão-resumo quer dizer.
      if (payload?.movimentoId) setFocusMov({ id: payload.movimentoId, token: Date.now(), status: payload.status })
    }
    else if (acao === 'lista') { setBucket(''); setPage(1) }
  }

  const cardDefs: Array<{ key: Bucket; label: string; v?: CardVal; tone: string }> = [
    { key: 'vencido',    label: 'Vencidos',    v: cards?.vencido,    tone: 'text-red-600 dark:text-red-400' },
    { key: 'vence_hoje', label: 'Vencem hoje', v: cards?.vence_hoje, tone: 'text-amber-600 dark:text-amber-400' },
    { key: 'a_vencer',   label: 'A vencer',    v: cards?.a_vencer,   tone: 'text-foreground' },
    { key: 'pago',       label: L.paidCard,    v: cards?.pago,       tone: 'text-primary' },
    { key: 'perda',      label: 'Perdas',      v: cards?.perda,      tone: 'text-red-600 dark:text-red-400' },
    { key: '',           label: 'Total do período', v: cards?.total, tone: 'text-foreground' },
  ]

  const TABS: Array<{ k: Tab; label: string; icon: typeof FiList }> = [
    { k: 'lancamentos', label: L.noun.charAt(0).toUpperCase() + L.noun.slice(1) + 's', icon: FiList },
    { k: 'conciliacao', label: 'Conciliação bancária', icon: FiRepeat },
    { k: 'regras', label: 'Regras de conciliação', icon: FiSliders },
  ]

  return (
    <PanelShell
      titulo={kind === 'despesa' ? 'Contas a pagar' : 'Contas a receber'}
      sub={kind === 'despesa' ? 'Vencimentos, pagamentos e a conciliação com o extrato do banco' : 'Vencimentos e recebimentos do período'}
      icone={kind === 'despesa' ? <FiArrowUpCircle /> : <FiArrowDownCircle />}
      acoes={kind === 'despesa' ? (
      /* Abas — conciliação e regras só existem no lado de despesa (extrato do banco) */
      <div className="neu-seg inline-flex items-center gap-1 self-start">
        {TABS.map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              tab === t.k ? 'neu-relief bg-primary/10 text-primary' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
    ) : undefined}>
      {tab === 'conciliacao' ? (
        /* Mesmo tratamento do lançamentos: largura total, box de altura cheia, scroll só na lista interna */
        <div className="flex-1 min-h-0 flex flex-col pln-corpo-tela">
          {/* A faixa mensal mora AQUI (27/07), não sobre os lançamentos: ela
              fala de extrato conciliado, que é o assunto desta aba. */}
          <FaixaConciliacao data={apont.data} onAcao={handleApontamento} />
          <ConciliacaoOfx
            authenticatedFetch={authenticatedFetch}
            onChanged={() => { reload(); apont.recarregar() }}
            focusMovement={focusMov ?? undefined}
          />
        </div>
      ) : tab === 'regras' ? (
        <div className="flex-1 min-h-0 flex flex-col pln-corpo-tela">
          <ReconciliationRules />
        </div>
      ) : (
      <div className="flex-1 min-h-0 flex flex-col pln-corpo-tela">
      {/* ═══ Resumo do período + apontamentos (2026-07-27) ═══
          Antes os 6 cards ocupavam a faixa inteira em uma linha. Agora dividem
          o espaço com a coluna de pendências: 3×2 à esquerda (~60%), coluna à
          direita (~40%). Em telas estreitas a coluna desce pra baixo dos cards
          em vez de espremer os valores — abaixo de lg vira uma coisa só. */}
      {/* `items-start`: sem ele o grid ESTICA os cards para acompanhar a altura
          da coluna de apontamentos ao lado — cada card ficava com 102px de
          altura para mostrar 45px de texto, e a faixa toda comia 212px, 22% da
          janela, sobrando só 6 linhas de tabela visíveis. */}
      {/* Respiro (21/10/2026, reclamação do dono): cards com 8×4px de padding e
          6px entre eles liam como planilha. Agora 14×12 e 10px — a faixa cresce
          ~30px, a tabela continua com a maior parte da altura. */}
      <div className="shrink-0 grid grid-cols-1 lg:grid-cols-5 gap-3 items-stretch">
      <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 ${kind === 'despesa' ? 'lg:col-span-3 lg:grid-cols-3' : 'lg:col-span-5 lg:grid-cols-6'}`}>
        {cardDefs.map(c => {
          const selectable = c.key !== '' || bucket !== ''
          const isActive = bucket === c.key && c.key !== ''
          // Balde vazio não some — sumir faria os cards dançarem de lugar a cada
          // troca de mês, e "não tem vencido" é informação. Mas perde o peso: em
          // preto, "Vencidos R$ 0,00" disputa a atenção de igual para igual com
          // "A vencer R$ 217.271,04", e o olho gasta leitura em zero.
          const vazio = (c.v?.count ?? 0) === 0
          return (
            <button
              key={c.label}
              type="button"
              disabled={!selectable || vazio}
              onClick={() => selectable && !vazio && setBucket(c.key)}
              title={vazio ? `Nenhum lançamento em ${c.label.toLowerCase()} neste período`
                : c.key ? `Filtrar por ${c.label.toLowerCase()}`
                : (selectable ? 'Limpar filtro de situação' : undefined)}
              // Sem filtro ativo, o card "Total" não filtra nada: tirar o hover e
              // o cursor evita o clique que não responde. Balde vazio idem — antes
              // ele filtrava e devolvia lista vazia, o que parecia defeito.
              //
              // O contorno vai em `outline` e não em `border` porque
              // `:root:not(.dark) .neu-lab .neu-card` força
              // `border-color: transparent !important` — medido no navegador, e
              // nenhuma classe de borda vence isso. O relevo neumórfico que
              // deveria substituir a borda também não renderiza aqui
              // (box-shadow: none), então o card ficava cinza sobre cinza e o
              // bloco inteiro lia como texto solto: a queixa de "muito vazio".
              className={`glass-panel rounded-xl border px-3.5 py-3 text-left transition-colors ${isActive ? 'neu-latch border-primary ring-1 ring-primary/40' : 'neu-card outline outline-1 -outline-offset-1 outline-black/[0.08] dark:outline-white/[0.10]'} ${vazio ? 'opacity-45 cursor-default' : selectable ? 'hover:border-primary/40 cursor-pointer' : 'cursor-default'}`}
            >
              <div className="text-[13px] font-medium text-muted-foreground leading-tight">{c.label}</div>
              {/* Valor e contagem na MESMA linha (27/07): eram três linhas por
                  card e o bloco todo comia meia dobra. A contagem é apoio do
                  valor, não informação de peso igual — daí o 10px ao lado. */}
              <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5 mt-1.5">
                <span className={`text-[19px] font-bold tracking-tight tabular-nums leading-tight ${vazio ? 'text-muted-foreground font-normal' : c.tone}`}>{brl(c.v?.amount ?? 0)}</span>
                <span className="text-[12.5px] text-muted-foreground leading-tight">{vazio ? '—' : `${c.v?.count} lanç.`}</span>
              </div>
            </button>
          )
        })}
        {/* O card soma o valor CHEIO do lançamento que casou com a busca, e a
            fatura rateada é um só lançamento: procurar "Combustíveis" em maio
            põe R$ 48.173,82 no card quando o Combustíveis de verdade é
            R$ 3.023,27 — a fatura entra inteira por causa de uma parte de
            R$ 1.642,15. Sem esta frase o número parece simplesmente errado.
            Ocupa a largura toda, abaixo dos cards. */}
        {rateioBusca && (
          <div className="col-span-2 md:col-span-3 neu-relief rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-400">
            Destes, <strong>{brl(rateioBusca.amount)}</strong> {rateioBusca.partes === 1 ? 'é uma parte de rateio' : `são ${rateioBusca.partes} partes de rateio`} dentro
            de {rateioBusca.lancamentos === 1 ? '1 lançamento' : `${rateioBusca.lancamentos} lançamentos`} — o card soma o valor cheio do lançamento, não o da parte.
          </div>
        )}
      </div>

        {kind === 'despesa' && (
          <div className="lg:col-span-2 min-h-0">
            <ApontamentosColuna
              data={apont.data} loading={apont.loading} erro={apont.erro}
              recarregar={apont.recarregar} onAcao={handleApontamento}
              kind={kind} year={anoFoco}
              onResolvido={() => { reload(); apont.recarregar() }}
            />
          </div>
        )}
      </div>


      {/* Toolbar — dentro da faixa de papel (PLANOS) */}
      <PlnBarra>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          <button onClick={() => setPeriodMode('mes')} title="Filtrar por mês de vencimento"
            className={`px-2.5 py-1.5 text-sm rounded-md transition-colors ${periodMode === 'mes' ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>Mês</button>
          <button onClick={() => setPeriodMode('custom')} title="Filtrar por intervalo de datas (vencimento De/Até)"
            className={`px-2.5 py-1.5 text-sm rounded-md transition-colors ${periodMode === 'custom' ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>Período</button>
        </div>
        {periodMode === 'mes' ? (
          /* Navegador de mês — mesmo padrão do Fluxo de caixa (pedido 2026-07-20) */
          <div className="flex items-center gap-2">
            <button onClick={() => setMonth(m => addMonth(m, -1))} title="Anterior"
              className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary"><FiChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-medium text-foreground min-w-[140px] text-center">{monthLabel(month)}</span>
            <button onClick={() => setMonth(m => addMonth(m, 1))} title="Próximo"
              className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary"><FiChevronRight className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>De</span>
            <input type="date" value={fromDate} max={toDate || undefined} onChange={e => setFromDate(e.target.value)}
              title="Início do período (vencimento)"
              className="neu-convex rounded-lg border border-border bg-background px-2 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
            <span>até</span>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={e => setToDate(e.target.value)}
              title="Fim do período (vencimento)"
              className="neu-convex rounded-lg border border-border bg-background px-2 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
          </div>
        )}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={`Buscar por descrição, ${L.party.toLowerCase()}, categoria ou item de rateio…`}
            className="neu-convex w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </div>
        {bucket && (
          <button onClick={() => setBucket('')} className="text-xs text-muted-foreground hover:text-foreground underline">limpar filtro</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {kind === 'despesa' && (
            <>
              <input ref={erpFileRef} type="file" accept=".xls,.xlsx,.xlsb,.csv,.htm,.html" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) onImportErp(f) }} />
              <button
                onClick={() => erpFileRef.current?.click()} disabled={importingErp}
                title='Importa o "Relatório de Movimento Bancário" do ERP — histórico já pago, reenviar substitui'
                className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 text-foreground hover:bg-secondary disabled:opacity-60"
              >
                {importingErp ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUploadCloud className="w-4 h-4" />} Importar histórico (ERP)
              </button>
              <button
                onClick={() => checkDuplicates(false)} disabled={dupChecking}
                title="Procura lançamentos repetidos (mesma conta, data, valor e tipo) e deixa você remover"
                className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 text-foreground hover:bg-secondary disabled:opacity-60"
              >
                {dupChecking ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCopy className="w-4 h-4" />} Duplicados
              </button>
              {/* Filtro por conta — mesma peça da Conciliação, pra a despesa e o
                  extrato serem filtrados do mesmo jeito. */}
              {accounts.length > 1 && (() => {
                const atual = accountFilter === '' ? null : accounts.find(a => a.id === accountFilter)
                return (
                  <div className="relative">
                    <button type="button" onClick={() => setAccountMenuOpen(o => !o)}
                      aria-haspopup="listbox" aria-expanded={accountMenuOpen} title="Filtrar por conta bancária"
                      className={`h-[38px] inline-flex items-center gap-2 rounded-lg border px-3 text-sm transition-colors ${
                        atual ? 'neu-latch border-primary bg-primary/10 text-primary font-medium' : 'border-border text-foreground hover:bg-secondary'}`}>
                      {atual && <BankLogo code={atual.bank_code} name={atual.name} type={atual.type} size={16} />}
                      <span className="truncate max-w-[140px]">{atual ? atual.name : 'Todas as contas'}</span>
                      <FiChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${accountMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {accountMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setAccountMenuOpen(false)} />
                        <div role="listbox" aria-label="Contas bancárias"
                          onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setAccountMenuOpen(false) } }}
                          className="neu-pop absolute right-0 top-full mt-1 w-60 glass-popover rounded-xl z-40 overflow-hidden py-1">
                          <button type="button" role="option" aria-selected={accountFilter === ''}
                            onClick={() => { setAccountFilter(''); setAccountMenuOpen(false) }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary ${accountFilter === '' ? 'text-primary font-medium' : ''}`}>
                            <span className="w-4 shrink-0 text-center">{accountFilter === '' ? '·' : ''}</span>
                            Todas as contas
                          </button>
                          <div className="max-h-64 overflow-y-auto scrollbar-hidden">
                            {accounts.map(a => (
                              <button key={a.id} type="button" role="option" aria-selected={accountFilter === a.id}
                                onClick={() => { setAccountFilter(a.id); setAccountMenuOpen(false) }}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary ${accountFilter === a.id ? 'text-primary font-medium' : ''}`}>
                                <BankLogo code={a.bank_code} name={a.name} type={a.type} size={16} />
                                <span className="truncate">{a.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )
              })()}
            </>
          )}
          {kind === 'receita' && (
            <button
              onClick={() => setShowVenda(true)}
              className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 text-foreground hover:bg-secondary"
            >
              <FiShoppingCart className="w-4 h-4" /> Nova venda
            </button>
          )}
          <button
            onClick={() => setModal({})}
            className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3 py-2 hover:opacity-90"
          >
            <FiPlus className="w-4 h-4" /> {L.newBtn}
          </button>
        </div>
      </PlnBarra>

      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}

      {erpMsg && (
        <div className={`shrink-0 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
          erpMsg.kind === 'ok' ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300'
          : erpMsg.kind === 'warn' ? 'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300'
          : 'border-red-500/30 bg-red-500/5 text-red-600 dark:text-red-400'}`}>
          <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{erpMsg.text}</span>
        </div>
      )}

      {/* Tabela — única região com scroll; cabeçalho sticky. O min-h garante
          janela útil no celular (cards+toolbar altos): o box degrada pro
          scroll de página, mas a tabela nunca fica espremida. */}
      <PlnBloco
        titulo="Lançamentos"
        nota={total > 0 ? `${items.length} de ${total} registro(s)${items.length >= total && total > PAGE_SIZE ? ' · fim da lista' : ''}` : undefined}
        className="flex-1 min-h-[320px]"
      >
      <div ref={listRef} onScroll={onListScroll}
        className="neu-table glass-panel flex-1 min-h-0 overflow-auto scrollbar-hidden">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>
                <button onClick={() => toggleSort('due_date')} title="Ordenar por vencimento"
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  Vencimento
                  {sort === 'due_date' && (dir === 'asc' ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />)}
                </button>
              </th>
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>Pagamento</th>
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Lançamento</th>
              <th className={`px-4 py-2.5 font-medium w-40 ${stickyTh}`}>Banco</th>
              <th className={`px-4 py-2.5 font-medium w-32 text-right ${stickyTh}`}>{L.owed}</th>
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>
                <button onClick={() => toggleSort('situacao')} title="Ordenar por situação (vencido → vence hoje → em aberto → pago)"
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  Situação
                  {sort === 'situacao' && (dir === 'asc' ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />)}
                </button>
              </th>
              <th className={`px-4 py-2.5 font-medium w-28 text-right ${stickyTh}`}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {/* O spinner só toma a tabela na PRIMEIRA leva. Ao puxar mais, a
                lista fica onde está e o aviso vai pro rodapé — senão a tela
                piscaria a cada rolagem. */}
            {loading && items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : !loading && items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Nenhuma {L.noun} neste período.</td></tr>
            ) : items.map(t => {
              const st = STATUS_STYLE[t.effective_status]
              const paid = t.effective_status === 'pago'
              const perda = t.effective_status === 'perda'
              const aberto = openSplits.has(t.id)
              return (
                <Fragment key={t.id}>
                <tr className="neu-tr border-b border-border/60 last:border-0 hover:bg-secondary/40">
                  <td className="px-4 py-1.5 text-foreground whitespace-nowrap align-top pt-2.5">{ptDate(t.due_date)}</td>
                  <td className="px-4 py-1.5 text-muted-foreground whitespace-nowrap align-top pt-2.5">{ptDate(t.payment_date)}</td>
                  <td className="px-4 py-1.5">
                    <div className="text-foreground">{t.description}
                      {t.installment_total > 1 && <span className="text-xs text-muted-foreground ml-1">({t.installment_no}/{t.installment_total})</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {t.reference_code === 'DDA' && (
                        <span title="Veio do DDA — boleto exportado do painel DDA"
                          className="neu-relief text-[11px] rounded bg-violet-500/15 text-violet-600 dark:text-violet-400 border border-violet-500/30 px-1.5 py-0.5 font-semibold">DDA</span>
                      )}
                      {t.reference_code === 'ASAAS' && (
                        <span title="Boleto de negociação emitido no Asaas (Conversas) — sincronizado automaticamente"
                          className="neu-relief text-[11px] rounded bg-sky-500/15 text-sky-600 dark:text-sky-400 border border-sky-500/30 px-1.5 py-0.5 font-semibold">ASAAS</span>
                      )}
                      {t.category_name && <span className="text-[11px] rounded bg-secondary px-1.5 py-0.5 text-muted-foreground">{t.category_name}</span>}
                      {t.party_name && <span className="text-[11px] rounded bg-secondary px-1.5 py-0.5 text-muted-foreground">{t.party_name}</span>}
                      {(t.split_count || 0) > 0 && (
                        <button onClick={() => toggleSplit(t.id)}
                          title={`Rateado em ${t.split_count} parte(s) — clique para abrir`}
                          className="neu-relief text-[11px] rounded bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30 px-1.5 py-0.5 font-semibold inline-flex items-center gap-1">
                          {aberto ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
                          Rateio ({t.split_count})
                        </button>
                      )}
                      {/* Sem este aviso, buscar "Combustíveis" traz uma fatura de
                          cartão que não diz "Combustíveis" em canto nenhum, e o
                          resultado é lido como bug. */}
                      {search && (t.split_match_count || 0) > 0 && (
                        <span className="text-[11px] rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 px-1.5 py-0.5">
                          encontrado no rateio · {t.split_match_count} parte(s) · {brl(t.split_match_amount || 0)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {t.bank_account_id ? (
                      <span className="inline-flex items-center gap-1.5 min-w-0" title={t.account_name || undefined}>
                        <BankLogo code={t.account_bank_code} name={t.account_name || undefined} type={t.account_type} size={20} />
                        <span className="text-xs text-muted-foreground truncate max-w-[110px]">{t.account_name}</span>
                      </span>
                    ) : <span className="text-muted-foreground/40">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-foreground whitespace-nowrap">{brl(t.amount)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {!paid && !perda && (
                        <button title={L.payTitle} onClick={() => setPayTarget(t)}
                          className="w-7 h-7 flex items-center justify-center rounded-md text-primary hover:bg-primary/10">
                          <FiCheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {!paid && (
                        <button title={perda ? 'Reverter perda' : 'Baixar como perda'} onClick={() => writeOff(t)}
                          className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10">
                          {perda ? <FiRotateCcw className="w-3.5 h-3.5" /> : <FiSlash className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      <button title="Editar" onClick={() => setModal({ txn: t })}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
                        <FiEdit2 className="w-3.5 h-3.5" />
                      </button>
                      <button title="Excluir" onClick={() => remove(t)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10">
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
                {/* Sub-linhas do rateio. Mesmas 7 colunas pra não desalinhar a
                    tabela. Situação e Ações ficam vazias de propósito: a parte
                    não tem vencimento nem status próprios e NÃO é pagável — um
                    botão ali convidaria a "pagar" um pedaço da fatura. */}
                {aberto && (t.splits || []).map((p, i) => (
                  <tr key={`${t.id}-s-${p.id ?? `sobra${i}`}`}
                      className={`border-b border-border/40 ${p.matched ? 'bg-amber-500/5' : 'bg-secondary/20'}`}>
                    <td /><td />
                    <td className="px-4 py-1.5 pl-10">
                      <span className="text-muted-foreground/50 mr-1">└</span>
                      <span className={p.category_id ? 'text-foreground' : 'italic text-muted-foreground'}>
                        {p.category_name || '(sem categoria)'}
                      </span>
                      {p.description && <span className="text-xs text-muted-foreground ml-2">{p.description}</span>}
                    </td>
                    <td />
                    <td className="px-4 py-1.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{brl(p.amount)}</td>
                    <td /><td />
                  </tr>
                ))}
                {aberto && (
                  <tr className="bg-secondary/20 border-b border-border/60">
                    <td colSpan={7} className="px-4 pl-10 pb-2 text-[11px] text-muted-foreground">
                      {t.split_count} parte(s) somam {brl(t.split_total || 0)} de {brl(t.amount)} — o valor do lançamento entra uma vez só nos totais.
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
            {loading && items.length > 0 && (
              <tr><td colSpan={7} className="px-4 py-4 text-center text-muted-foreground">
                <FiLoader className="w-4 h-4 animate-spin inline" />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      </PlnBloco>
      {/* Contagem — os botões de página saíram com o scroll infinito; a
          contagem agora mora no cabeçalho do bloco (PLANOS: nada solto no chão). */}

      {modal && (
        <TxnModal kind={kind} txn={modal.txn} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload() }} />
      )}
      {payTarget && (
        <PayModal kind={kind} txn={payTarget} onClose={() => setPayTarget(null)} onDone={() => { setPayTarget(null); reload() }} />
      )}
      {showVenda && (
        <NovaVenda onClose={() => setShowVenda(false)} onSaved={() => { setShowVenda(false); reload() }} />
      )}

      {/* Duplicados — modal flutuante. Mostra grupo a grupo, com a descrição de
          cada cópia: é ela que denuncia o par (uma trunca o CPF, a outra traz
          completo). Cross-lote vem em destaque e já pré-marcado; mesmo lote
          aparece sem marcação, porque costuma ser pagamento legítimo repetido. */}
      {dupOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => { if (!dupBusy) setDupOpen(false) }}>
          <div className="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl border border-border bg-background shadow-xl overflow-hidden"
            onClick={e => e.stopPropagation()}>
            {/* PLANOS (21/10/2026): cabeçalho 16px, aviso legível (era 11px) e
                grupos com respiro; mecânica de abrir/fechar intacta. */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-base font-bold flex items-center gap-2.5">
                <FiCopy className="w-[18px] h-[18px] text-primary" /> Lançamentos duplicados
              </h3>
              <button onClick={() => setDupOpen(false)} disabled={dupBusy}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-secondary disabled:opacity-50" title="Fechar">
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-3 text-[13px] leading-relaxed text-muted-foreground border-b border-border">
              Mesma conta, data, valor e tipo. Repetição não é prova de erro — dois PIX
              iguais no mesmo dia acontecem. O que veio de <strong>importações diferentes</strong> está
              destacado e já marcado; confira antes de remover.
            </div>

            <div className="flex-1 overflow-auto scrollbar-hidden p-4 space-y-3 bg-secondary/30">
              {!dupGroups?.length && (
                <p className="text-sm text-muted-foreground text-center py-8">Nenhum lançamento duplicado.</p>
              )}
              {dupGroups?.map((g, gi) => (
                <div key={gi} className={`rounded-xl border px-4 py-3 ${g.lotes > 1 ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-background'}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap text-[13px] mb-2">
                    <span className="font-medium">
                      {ptDate(g.payment_date)} · {brl(g.amount)} · {g.kind}
                      {g.bank_account_name ? ` · ${g.bank_account_name}` : ''}
                    </span>
                    <span className={g.lotes > 1 ? 'text-amber-600 font-medium' : 'text-muted-foreground'}>
                      {g.copias} cópias{g.lotes > 1 ? ` · ${g.lotes} importações` : ' · mesma importação'}
                    </span>
                  </div>
                  {g.rows.map(r => (
                    <label key={r.id}
                      className={`flex items-start gap-2.5 text-[13px] py-1.5 px-2 rounded-lg ${r.conciliada ? 'opacity-60' : 'hover:bg-secondary cursor-pointer'}`}>
                      <input type="checkbox" className="neu-check mt-0.5" disabled={!!r.conciliada}
                        checked={dupSel.has(r.id)}
                        onChange={e => setDupSel(prev => {
                          const n = new Set(prev)
                          if (e.target.checked) n.add(r.id); else n.delete(r.id)
                          return n
                        })} />
                      <span className="flex-1 min-w-0">
                        <span className="block truncate">{r.description || '(sem descrição)'}</span>
                        <span className="text-xs text-muted-foreground">
                          #{r.id} · {r.reference_code || 'conciliação'} · importado em {ptDate(String(r.created_at).slice(0, 10))}
                          {r.conciliada ? ' · conciliado (não removível)' : ''}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            <div className="px-5 py-3.5 border-t border-border flex items-center justify-between gap-3">
              <span className="text-[13px] text-muted-foreground">
                {dupMsg || `${dupSel.size} selecionado(s)`}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setDupOpen(false)} disabled={dupBusy}
                  className="neu-tab h-9 px-3.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary disabled:opacity-50">
                  Fechar
                </button>
                <button onClick={removeDuplicates} disabled={dupBusy || !dupSel.size}
                  className="neu-relief inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-500 disabled:opacity-50">
                  {dupBusy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiTrash2 className="w-4 h-4" />}
                  Remover selecionados
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
      )}
    </PanelShell>
  )
}

// ── Modal Nova/Editar despesa|receita ────────────────────────────────
// ── Modal de baixa (pagar/receber) ───────────────────────────────────
function PayModal({ kind, txn, onClose, onDone }: { kind: Kind; txn: Txn; onClose: () => void; onDone: () => void }) {
  const { authenticatedFetch } = useAuth()
  const L = LABELS[kind]
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const confirm = async () => {
    setSaving(true); setErr(null)
    try {
      const r = await authenticatedFetch(`/api/transactions/${txn.id}/pay`, { method: 'POST', body: JSON.stringify({ payment_date: date }) })
      const d = await r.json()
      if (!d.success) { setErr(d.error || 'Falha'); return }
      onDone()
    } catch { setErr('Erro de conexão') }
    finally { setSaving(false) }
  }

  return (
    <SideModal
      title={`Confirmar ${L.payTitle.toLowerCase()}`}
      subtitle={`${txn.description} · ${brl(txn.amount)}`}
      icon={FiCheckCircle}
      onClose={onClose}
      maxWidth="max-w-md"
      footer={
        <>
          <button onClick={onClose} className="neu-tab h-9 px-3.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button onClick={confirm} disabled={saving}
            className="neu-relief inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />} Confirmar
          </button>
        </>
      }
    >
      {/* PLANOS (21/10/2026): o campo mora numa caixa de papel com respiro,
          igual às seções das outras gavetas. */}
      {err && <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400">{err}</div>}
      <div className="neu-card rounded-xl border border-border bg-card p-4">
        <label className="block text-[13px] font-medium text-foreground mb-1.5">Data do {kind === 'despesa' ? 'pagamento' : 'recebimento'}</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputCls} />
      </div>
    </SideModal>
  )
}
